// The score of a game's opening and middlegame for a library row, read with
// the same rules the review's stage header uses, so the row and the review
// can never disagree: every score from the reader's side; the opening starts
// level and its score is the position after the reader's first move out of
// it; the middlegame's is the change from its first position to the reader's
// first move after it. Undefined when the game is not scanned yet, when the
// reader played neither side, or when the game ended before the stage.
import { type Color, type Fen, type ReviewPhases } from '@chess/api-model';
import { openingExit, phaseRange, readerPawns, readerScoreText, scoreForReader, sideToMove, type ScoredPosition } from '@chess/api-review';

export interface StageScore {
  /** In the score bar's notation, or a mate in words. */
  text: string;
  tone: 'good' | 'bad' | 'level';
}

export interface StageScores {
  opening?: StageScore;
  middlegame?: StageScore;
}

const toneOf = (swing: number): StageScore['tone'] => (swing > 0.005 ? 'good' : swing < -0.005 ? 'bad' : 'level');

export function stageScores(
  scan: { positions: ReadonlyArray<ScoredPosition> } | undefined,
  game: { moveCount: number; positions: ReadonlyArray<Fen> },
  phases: ReviewPhases,
  reader: Color | undefined,
): StageScores {
  if (!scan || !reader) return {};
  const at = (ply: number) => scan.positions.find((p) => p.ply === ply);
  const out: StageScores = {};

  const exit = openingExit(scan, phases, game.moveCount, reader);
  if (exit.score) out.opening = { text: readerScoreText(exit.score), tone: toneOf(readerPawns(exit.score)) };

  const { fromPly, toPly } = phaseRange(phases, game.moveCount, 'middlegame', reader);
  if (toPly > fromPly) {
    const first = at(fromPly);
    const last = at(toPly);
    if (first && last) {
      const start = scoreForReader(first.score, sideToMove(first.fen), reader);
      const end = scoreForReader(last.score, sideToMove(last.fen), reader);
      const swing = readerPawns(end) - readerPawns(start);
      const mate = start.mate !== undefined || end.mate !== undefined;
      const text = mate ? (swing > 0 ? 'to a mate' : swing < 0 ? 'to being mated' : '+0.00') : readerScoreText({ cp: Math.round(swing * 100) });
      out.middlegame = { text, tone: toneOf(swing) };
    }
  }
  return out;
}
